import { Controller } from "@hotwired/stimulus";

// We'll use dynamic import to get the Dropzone class
let DropzoneClass: any;

// Connects to data-controller="dropzone"
export default class extends Controller {
  static targets = ["input", "form", "submit"];

  // Define target types
  declare readonly inputTarget: HTMLElement;
  declare readonly formTarget: HTMLFormElement;
  declare readonly submitTarget: HTMLButtonElement;
  declare readonly hasSubmitTarget: boolean;

  dropzone: any;

  // Configuration
  maxFiles = 7;
  validFileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

  async connect() {
    // Dynamically import Dropzone
    if (!DropzoneClass) {
      try {
        // @ts-ignore - Ignore TypeScript errors for dynamic imports
        const dropzoneModule = await import("dropzone");
        DropzoneClass = dropzoneModule.default;
        // Disable auto discovery after loading
        DropzoneClass.autoDiscover = false;
      } catch (error) {
        console.error("Failed to load Dropzone", error);
        return;
      }
    }

    // Only initialize if dropzone elements exist
    if (!this.inputTarget) return;

    // Clean up any existing dropzone instances
    if ((this.inputTarget as any).dropzone) {
      (this.inputTarget as any).dropzone.destroy();
    }

    const mainContentBody = document.querySelector(".main-content__body");
    const validFileTypeIcon =
      mainContentBody?.getAttribute("data-icon-url") || "";
    const maxFilesExceededError = `You can only upload ${this.maxFiles} files. Some of your selected files have been excluded.`;

    this.dropzone = new DropzoneClass(this.inputTarget, {
      url: "/admin/assessments",
      acceptedFiles: ".xlsx",
      dictDefaultMessage: "Click to upload or drop all .xlsx instruments here",
      dictMaxFilesExceeded: maxFilesExceededError,
      autoProcessQueue: false,
      uploadMultiple: true,
      maxFiles: this.maxFiles,
      parallelUploads: this.maxFiles,
      maxFilesize: 1,
      paramName: "import_assessment[files]",
      addRemoveLinks: true,
      headers: {
        "X-CSRF-Token":
          document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute("content") || "",
      },
    });

    // Set up event handlers
    this.dropzone.on("addedfile", this.handleAddedFile.bind(this));
    this.dropzone.on("error", this.handleError.bind(this));
    this.dropzone.on("sending", this.appendFormParams.bind(this));
    this.dropzone.on("success", this.handleSuccess.bind(this));

    // Style the dropzone
    this.styleDropzone();

    // Connect the submit button if it exists
    if (this.hasSubmitTarget) {
      this.submitTarget.addEventListener("click", this.handleSubmit.bind(this));
    }
  }

  disconnect() {
    // Clean up dropzone when controller disconnects
    if (this.dropzone) {
      this.dropzone.destroy();
    }
  }

  handleAddedFile(file: any) {
    this.removeProgressBar();
    this.displayValidFileTypeIcon(file);
    this.displayFileNameAtTop(file);

    // Handle max files exceeded
    if (this.dropzone.files.length > this.maxFiles) {
      this.dropzone.removeFile(this.dropzone.files[0]);
      this.flashMessage(
        "flash-error",
        `You can only upload ${this.maxFiles} files. Some of your selected files have been excluded.`,
      );
    }
  }

  handleError(file: any, responseText: any) {
    this.requeueAllFiles();
    this.removeErrorIcons();
    this.flashMessage("flash-error", this.determineError(file, responseText));
  }

  determineError(file: any, responseText: any) {
    if (file.xhr === undefined) {
      return responseText;
    } else {
      return this.displayServerError(responseText);
    }
  }

  displayServerError(responseText: any) {
    if (responseText.error === undefined) {
      return "An unknown error has occurred. Secure Halo support has been notified by email.";
    } else {
      return responseText.error;
    }
  }

  appendFormParams(file: any, xhr: any, formData: FormData) {
    const assessmentTypeEl = document.getElementById(
      "import_assessment_assessment_type",
    ) as HTMLInputElement;
    const companyEl = document.getElementById(
      "import_assessment_company",
    ) as HTMLInputElement;

    if (assessmentTypeEl && companyEl) {
      formData.append(
        "import_assessment[assessment_type]",
        assessmentTypeEl.value,
      );
      formData.append("import_assessment[company]", companyEl.value);
    }
  }

  handleSuccess(file: any, responseText: any) {
    if (responseText.success_url) {
      window.location.href = responseText.success_url;
    }
  }

  handleSubmit(event: Event) {
    event.preventDefault();
    this.showSubmitMessage();
    this.dropzone.processQueue();

    const removeButtons = document.querySelectorAll(".dz-remove");
    removeButtons.forEach((button) => {
      button.textContent = "Remove file";
    });
  }

  styleDropzone() {
    const dropzoneEl = this.inputTarget;
    dropzoneEl.style.borderStyle = "dashed";

    dropzoneEl.addEventListener("dragover", (event) => {
      const dzMessage = document.querySelector(".dz-message");
      if (dzMessage instanceof HTMLElement) dzMessage.style.opacity = "0.5";

      dropzoneEl.style.backgroundColor = "#E5EDF8";
      dropzoneEl.style.boxShadow = "";
      dropzoneEl.style.borderStyle = "solid";
    });

    dropzoneEl.addEventListener("mouseout", (event) => {
      dropzoneEl.style.boxShadow = "";
      dropzoneEl.style.borderStyle = "dashed";
    });

    dropzoneEl.addEventListener("mouseover", (event) => {
      dropzoneEl.style.boxShadow = "0px 0px 6px rgba(0, 0, 0, 0.2)";
      dropzoneEl.style.borderStyle = "solid";
    });

    dropzoneEl.addEventListener("dragleave", (event) => {
      const dzMessage = document.querySelector(".dz-message");
      if (dzMessage instanceof HTMLElement) dzMessage.style.opacity = "1.0";

      dropzoneEl.style.borderStyle = "dashed";
      dropzoneEl.style.backgroundColor = "#ffffff";
    });

    dropzoneEl.addEventListener("drop", (event) => {
      const dzMessage = document.querySelector(".dz-message");
      if (dzMessage instanceof HTMLElement) dzMessage.style.opacity = "1.0";

      dropzoneEl.style.borderStyle = "dashed";
      dropzoneEl.style.backgroundColor = "#ffffff";
    });
  }

  flashMessage(klass: string, message: string) {
    this.removeFlashes();
    const mainContent = document.querySelector(".main-content__body");

    if (mainContent && mainContent.parentNode) {
      const flashesDiv = document.createElement("div");
      flashesDiv.className = "flashes";

      const flashDiv = document.createElement("div");
      flashDiv.className = "flash " + klass;
      flashDiv.textContent = message;

      flashesDiv.appendChild(flashDiv);
      mainContent.parentNode.insertBefore(flashesDiv, mainContent);
    }
  }

  removeProgressBar() {
    const progressBars = document.querySelectorAll(".dz-progress");
    progressBars.forEach((bar) => {
      bar.remove();
    });
  }

  displayValidFileTypeIcon(file: any) {
    if (file.type === this.validFileType) {
      const mainContentBody = document.querySelector(".main-content__body");
      const iconUrl = mainContentBody?.getAttribute("data-icon-url");
      if (iconUrl) {
        this.dropzone.emit("thumbnail", file, iconUrl);
      }
    }
  }

  displayFileNameAtTop(file: any) {
    const previews = document.getElementsByClassName("dz-preview");
    if (previews.length > 0) {
      const preview = previews[previews.length - 1];

      const fileName = document.createElement("div");
      fileName.className = "dz-file-name";
      fileName.innerHTML = file.name;

      preview.insertBefore(fileName, preview.firstChild);
    }
  }

  removeFlashes() {
    const flashes = document.querySelectorAll(".flashes");
    flashes.forEach((flash) => {
      flash.remove();
    });
  }

  requeueAllFiles() {
    this.dropzone.files.forEach((file: any) => {
      file.status = "queued"; // This matches Dropzone.QUEUED
    });
  }

  removeErrorIcons() {
    const previews = document.querySelectorAll(".dz-preview");
    previews.forEach((preview) => {
      preview.classList.remove("dz-error");
    });
  }

  showSubmitMessage() {
    this.removeFlashes();
    if (this.dropzone.files.length) {
      this.flashMessage("flash-notice", "Processing...");
    } else {
      this.flashMessage("flash-error", "You must add files to be uploaded");
    }
  }
}
